/**
 * Deterministic evaluation engine.
 *
 * Every score, subtotal, total and rank rendered in the Evaluation workspace
 * is produced here from (a) the published weightings and (b) the current
 * assessment bands. Nothing is hardcoded — adjusting a band at moderation
 * genuinely recomputes the award recommendation, which is the whole point of
 * a defensible evaluation record.
 */
import {
  criteria, bidders, rawScores, complianceResults, scoringMethodology,
  assessorMatrix, panel, riskFactors, type Band,
} from '../data/evaluationMocks';

export const MAX_BAND = 4;

export const qualityCriteria = criteria.filter(c => c.kind === 'quality');
export const priceCriterion = criteria.find(c => c.kind === 'price')!;
export const allSubCriteria = qualityCriteria.flatMap(c => c.sub.map(s => ({ ...s, parentId: c.id, parentName: c.name })));

export const totalQualityWeight = qualityCriteria.reduce((a, c) => a + c.weight, 0);
export const priceWeight = priceCriterion.weight;

export type BandMap = Record<string, Record<string, Band>>;

/** Starting bands, taken from the assessors' output. */
export function initialBands(): BandMap {
  const out: BandMap = {};
  Object.entries(rawScores).forEach(([bidderId, entries]) => {
    out[bidderId] = {};
    entries.forEach(e => { out[bidderId][e.sub] = e.band; });
  });
  return out;
}

/** A bidder is compliant only if no mandatory requirement is failed. */
export function isCompliant(bidderId: string): boolean {
  return !(complianceResults[bidderId] || []).some(r => r.status === 'fail');
}

export function failedChecks(bidderId: string): number[] {
  return (complianceResults[bidderId] || []).reduce<number[]>((acc, r, i) => {
    if (r.status === 'fail') acc.push(i);
    return acc;
  }, []);
}

export const compliantBidders = () => bidders.filter(b => isCompliant(b.id));

/** band ÷ 4 × weight — the standard banded-scale conversion. */
export function pointsFor(band: Band, weight: number): number {
  return (band / MAX_BAND) * weight;
}

export interface CriterionResult {
  id: string; name: string; weight: number; points: number;
  sub: { id: string; name: string; weight: number; band: Band; points: number }[];
}

export function qualityBreakdown(bidderId: string, bands: BandMap): CriterionResult[] {
  return qualityCriteria.map(c => {
    const sub = c.sub.map(s => {
      const band = (bands[bidderId]?.[s.id] ?? 0) as Band;
      return { id: s.id, name: s.name, weight: s.weight, band, points: pointsFor(band, s.weight) };
    });
    return { id: c.id, name: c.name, weight: c.weight, points: sub.reduce((a, s) => a + s.points, 0), sub };
  });
}

export function qualityTotal(bidderId: string, bands: BandMap): number {
  return qualityBreakdown(bidderId, bands).reduce((a, c) => a + c.points, 0);
}

/**
 * Price score. Lowest COMPLIANT price sets the benchmark — an excluded
 * tender never influences the price scores of the tenders that remain.
 */
export function priceScore(bidderId: string): number {
  const compliant = compliantBidders();
  if (!compliant.length || !isCompliant(bidderId)) return 0;
  const lowest = Math.min(...compliant.map(b => b.price));
  const me = bidders.find(b => b.id === bidderId)!;
  return (lowest / me.price) * priceWeight;
}

export interface RankedResult {
  bidderId: string; name: string; shortName: string; price: number;
  quality: number; priceScore: number; total: number; rank: number;
  gapToLeader: number; compliant: boolean;
  disqualified: boolean; disqualifiedFor: string[];
}

/**
 * The published methodology lists disqualification triggers. They are checked
 * live: if moderation drops a band to 0 or 1, or pushes quality below the
 * gateway, the tender falls out of the award recommendation there and then.
 */
export function disqualificationTriggers(bidderId: string, bands: BandMap): string[] {
  const out: string[] = [];
  const floor = scoringMethodology.thresholds.sub_criterion_floor;
  const low = allSubCriteria.filter(s => (bands[bidderId]?.[s.id] ?? 4) < floor);
  if (low.length) {
    out.push(`Band below ${floor} on ${low.map(l => l.id).join(', ')} — sub-criterion floor not met`);
  }
  const q = qualityTotal(bidderId, bands);
  if (q < scoringMethodology.minimum_qualifying_score) {
    out.push(`Quality score ${q.toFixed(2)} is below the ${scoringMethodology.minimum_qualifying_score}-point quality gateway`);
  }
  const b = bidders.find(x => x.id === bidderId);
  if (b && b.price > scoringMethodology.thresholds.affordability_ceiling) {
    out.push('Tendered price exceeds the published affordability ceiling');
  }
  return out;
}

export function rankAll(bands: BandMap): RankedResult[] {
  const rows: RankedResult[] = compliantBidders().map(b => {
    const quality = qualityTotal(b.id, bands);
    const ps = priceScore(b.id);
    const dq = disqualificationTriggers(b.id, bands);
    return {
      bidderId: b.id, name: b.name, shortName: b.shortName, price: b.price,
      quality, priceScore: ps, total: quality + ps, rank: 0, gapToLeader: 0, compliant: true,
      disqualified: dq.length > 0, disqualifiedFor: dq,
    };
  });

  // Disqualified tenders sort last and carry no rank.
  rows.sort((a, b) => {
    if (a.disqualified !== b.disqualified) return a.disqualified ? 1 : -1;
    if (b.total !== a.total) return b.total - a.total;
    // Published tie-break: quality, then Q2, then lower price.
    if (b.quality !== a.quality) return b.quality - a.quality;
    const q2 = (id: string) => qualityBreakdown(id, bands).find(c => c.id === 'Q2')?.points ?? 0;
    if (q2(b.bidderId) !== q2(a.bidderId)) return q2(b.bidderId) - q2(a.bidderId);
    return a.price - b.price;
  });

  const live = rows.filter(r => !r.disqualified);
  const leader = live[0]?.total ?? 0;
  live.forEach((r, i) => { r.rank = i + 1; r.gapToLeader = leader - r.total; });
  return rows;
}

/** Was the award decided by the published tie-break rather than on points? */
export function tieBreakApplied(bands: BandMap): boolean {
  const live = rankAll(bands).filter(r => !r.disqualified);
  return live.length >= 2 && Math.abs(live[0].total - live[1].total) < 0.005;
}

/** Individual assessor bands vs the moderated consensus. */
export interface Divergence { bidderId: string; sub: string; a: Band; b: Band; consensus: Band; note?: string; spread: number }

export function divergences(bands: BandMap): Divergence[] {
  const out: Divergence[] = [];
  Object.entries(assessorMatrix).forEach(([bidderId, subs]) => {
    Object.entries(subs).forEach(([sub, e]) => {
      const spread = Math.abs(e.a - e.b);
      if (spread > 0) {
        out.push({ bidderId, sub, a: e.a, b: e.b, consensus: (bands[bidderId]?.[sub] ?? e.a) as Band, note: e.note, spread });
      }
    });
  });
  return out.sort((x, y) => y.spread - x.spread);
}

export const moderator = panel.find(p => p.moderator);
export const assessors = panel.filter(p => !p.moderator);

export const excludedBidders = () => bidders.filter(b => !isCompliant(b.id));

/**
 * How close is the result? Reports the winning margin and the smallest set of
 * band uplifts that would put the runner-up ahead — the question a panel is
 * always asked and can rarely answer quickly.
 */
export interface Sensitivity {
  margin: number;
  leader: string;
  challenger: string;
  bandsNeeded: number;
  uplifts: { sub: string; name: string; from: Band; to: Band; gain: number }[];
  decisive: boolean;
}

export function sensitivity(bands: BandMap): Sensitivity | null {
  const ranked = rankAll(bands).filter(r => !r.disqualified);
  if (ranked.length < 2) return null;
  const [leader, challenger] = ranked;
  const margin = leader.total - challenger.total;

  // Cheapest route to close the gap: take the challenger's sub-criteria with
  // headroom, best points-per-band first.
  const headroom = allSubCriteria
    .map(s => {
      const band = (bands[challenger.bidderId]?.[s.id] ?? 0) as Band;
      return { sub: s.id, name: s.name, from: band, to: MAX_BAND as Band, gain: pointsFor(MAX_BAND as Band, s.weight) - pointsFor(band, s.weight) };
    })
    .filter(s => s.gain > 0)
    .sort((a, b) => b.gain - a.gain);

  const uplifts: Sensitivity['uplifts'] = [];
  let closed = 0;
  for (const h of headroom) {
    if (closed > margin) break;
    uplifts.push(h);
    closed += h.gain;
  }

  return {
    margin,
    leader: leader.shortName,
    challenger: challenger.shortName,
    bandsNeeded: uplifts.reduce((a, u) => a + (u.to - u.from), 0),
    uplifts,
    decisive: closed <= margin ? false : uplifts.length >= 2,
  };
}

const gbp = (n: number) => `£${n.toLocaleString('en-GB')}`;
const pt = (n: number) => n.toFixed(2);

/**
 * Assessment summary — the statutory artefact under the Procurement Act 2023.
 * Each unsuccessful supplier receives their own scores with reasons, plus the
 * successful supplier's scores, before the standstill period begins.
 */
export function buildAssessmentSummary(bidderId: string, bands: BandMap, tender: any): string {
  const ranked = rankAll(bands).filter(r => !r.disqualified);
  const me = ranked.find(r => r.bidderId === bidderId);
  const winner = ranked[0];
  if (!me) return '';
  const breakdown = qualityBreakdown(bidderId, bands);
  const entries = rawScores[bidderId] || [];
  const entryFor = (sub: string) => entries.find(e => e.sub === sub);

  const lines: string[] = [];
  lines.push(`# Assessment Summary`);
  lines.push(`**Procurement:** ${tender.title} · ${tender.reference}`);
  lines.push(`**Contracting authority:** ${tender.buyer}`);
  lines.push(`**Supplier:** ${me.name}`);
  lines.push(`**Award basis:** ${tender.awardBasis} under the ${tender.regime}`);
  lines.push(`**Outcome:** ${me.rank === 1 ? 'Successful — most advantageous tender' : `Unsuccessful — ranked ${me.rank} of ${ranked.length}`}`);
  lines.push('');
  lines.push(`> Issued under section 50 of the Procurement Act 2023 ahead of publication of the contract award notice. A standstill period of 8 working days applies from the date of publication.`);
  lines.push('');
  lines.push(`## 1. Your score`);
  lines.push('');
  lines.push(`| Criterion | Weighting | Your score |`);
  lines.push(`| --- | ---: | ---: |`);
  breakdown.forEach(c => lines.push(`| ${c.id} · ${c.name} | ${c.weight}% | ${pt(c.points)} |`));
  lines.push(`| ${priceCriterion.id} · Price | ${priceWeight}% | ${pt(me.priceScore)} |`);
  lines.push(`| **Total** | **100%** | **${pt(me.total)}** |`);
  lines.push('');
  lines.push(`Tendered price: **${gbp(me.price)}**. Price is scored as ${priceCriterion.method?.toLowerCase()}.`);
  lines.push('');

  if (me.rank !== 1) {
    lines.push(`## 2. The successful tender`);
    lines.push('');
    lines.push(`| Criterion | Weighting | ${winner.shortName} | You |`);
    lines.push(`| --- | ---: | ---: | ---: |`);
    const wb = qualityBreakdown(winner.bidderId, bands);
    breakdown.forEach((c, i) => lines.push(`| ${c.id} · ${c.name} | ${c.weight}% | ${pt(wb[i].points)} | ${pt(c.points)} |`));
    lines.push(`| ${priceCriterion.id} · Price | ${priceWeight}% | ${pt(winner.priceScore)} | ${pt(me.priceScore)} |`);
    lines.push(`| **Total** | **100%** | **${pt(winner.total)}** | **${pt(me.total)}** |`);
    lines.push('');
    lines.push(`The successful tender scored **${pt(winner.total - me.total)} points** higher than yours.`);
    lines.push('');
  }

  lines.push(`## ${me.rank === 1 ? '2' : '3'}. Reasons for the score awarded`);
  lines.push('');
  breakdown.forEach(c => {
    lines.push(`### ${c.id} · ${c.name} — ${pt(c.points)} of ${c.weight}`);
    lines.push('');
    c.sub.forEach(s => {
      const e = entryFor(s.id);
      lines.push(`**${s.id} ${s.name}** — band ${s.band}/4, ${pt(s.points)} of ${s.weight} points`);
      lines.push('');
      if (e) {
        lines.push(e.rationale);
        lines.push('');
        if (e.strengths.length) lines.push(`*Strengths:* ${e.strengths.join('; ')}.`);
        if (e.concerns.length) lines.push(`*Areas of concern:* ${e.concerns.join('; ')}.`);
        if (e.evidence.length) {
          lines.push('');
          e.evidence.forEach(ev => lines.push(`> "${ev.quote}"  \n> — ${ev.doc}, ${ev.clause}, p.${ev.page}`));
        }
        lines.push('');
      }
    });
  });

  lines.push(`## ${me.rank === 1 ? '3' : '4'}. Standstill and next steps`);
  lines.push('');
  lines.push(`A standstill period of 8 working days begins on publication of the contract award notice on 2 September 2026 and ends on 11 September 2026. During this period you may request further information or raise concerns with the contracting authority.`);
  lines.push('');
  lines.push(`Contact: procurement@aldercroft.gov.uk · Reference ${tender.reference}`);
  return lines.join('\n');
}

/* ==========================================================================
 * Post-award documents
 * --------------------------------------------------------------------------
 * Which obligations bite depends on the contract value. Under the Procurement
 * Act 2023 a contract details notice is required within 30 days of entering
 * the contract, but publishing the contract itself and setting at least three
 * KPIs (s.52) apply only above £5 million.
 * ======================================================================== */

export const KPI_THRESHOLD = 5_000_000;

export interface Obligation {
  id: string;
  label: string;
  basis: string;
  due: string;
  applies: boolean;
  why?: string;
}

export function postAwardObligations(contractValue: number): Obligation[] {
  const above = contractValue > KPI_THRESHOLD;
  return [
    {
      id: 'O1', label: 'Contract details notice', basis: 'PA 2023 s.53',
      due: 'Within 30 days of entering the contract', applies: true,
    },
    {
      id: 'O2', label: 'Publish a copy of the contract', basis: 'PA 2023 s.53(3)',
      due: 'With the contract details notice', applies: above,
      why: above ? undefined : `Applies above £5m. This contract is ${gbp(contractValue)}.`,
    },
    {
      id: 'O3', label: 'Set at least three key performance indicators', basis: 'PA 2023 s.52',
      due: 'Before entering the contract', applies: above,
      why: above ? undefined : `Applies above £5m. This contract is ${gbp(contractValue)}.`,
    },
    {
      id: 'O4', label: 'Payments compliance notice', basis: 'PA 2023 s.69',
      due: 'Within 30 days of the end of each reporting period', applies: true,
    },
    {
      id: 'O5', label: 'Contract termination notice', basis: 'PA 2023 s.80',
      due: 'Within 30 days of expiry or termination', applies: true,
    },
  ];
}

export function buildAwardLetter(bidderId: string, bands: BandMap, tender: any): string {
  const ranked = rankAll(bands).filter(r => !r.disqualified);
  const me = ranked.find(r => r.bidderId === bidderId);
  if (!me) return '';
  const b = bidders.find(x => x.id === bidderId)!;
  const L: string[] = [];
  L.push(`# Letter of Award`);
  L.push('');
  L.push(`**${tender.buyer}**`);
  L.push(`Procurement reference ${tender.reference}`);
  L.push('');
  L.push(`**To:** ${b.name}, ${b.hq} (company number ${b.companyNumber})`);
  L.push('');
  L.push(`Dear Sir or Madam`);
  L.push('');
  L.push(`## ${tender.title}`);
  L.push('');
  L.push(`Following the standstill period, which ended on 11 September 2026 without challenge, I am pleased to confirm that ${tender.buyer} accepts your tender dated 14 August 2026 for the above contract.`);
  L.push('');
  L.push(`| | |`);
  L.push(`| --- | --- |`);
  L.push(`| Contract | ${tender.title} |`);
  L.push(`| Reference | ${tender.reference} |`);
  L.push(`| Award basis | ${tender.awardBasis} under the ${tender.regime} |`);
  L.push(`| Contract price | ${gbp(me.price)} |`);
  L.push(`| Total score | ${pt(me.total)} of 100 (quality ${pt(me.quality)}, price ${pt(me.priceScore)}) |`);
  L.push(`| Term | ${tender.term} |`);
  L.push(`| Commencement | 1 October 2026 |`);
  L.push('');
  L.push(`## 1. Contract formation`);
  L.push('');
  L.push(`This letter, together with the Invitation to Tender and its schedules, your tender of 14 August 2026 and the clarifications exchanged during the evaluation, forms the contract between us. No further document is required for the contract to take effect.`);
  L.push('');
  L.push(`## 2. Commitments carried into the contract`);
  L.push('');
  L.push(`The following commitments made in your tender are incorporated as contractual obligations and will be managed as such:`);
  L.push('');
  const entries = rawScores[bidderId] || [];
  entries.filter(e => e.strengths.length).forEach(e => {
    const sub = allSubCriteria.find(s => s.id === e.sub);
    e.strengths.forEach(st => L.push(`- **${e.sub} ${sub?.name}** — ${st}`));
  });
  L.push('');
  L.push(`## 3. Conditions of award`);
  L.push('');
  const risks = riskFactors.filter(r => r.affects.includes(bidderId));
  if (risks.length) {
    risks.forEach(r => {
      L.push(`**${r.risk_id} · ${r.category}** — ${r.description}`);
      L.push('');
      r.mitigation_required.forEach(m => L.push(`- ${m}`));
      L.push('');
    });
  } else {
    L.push(`No conditions are attached to this award.`);
    L.push('');
  }
  L.push(`## 4. Mobilisation`);
  L.push('');
  L.push(`Please confirm acceptance in writing within 10 working days. A mobilisation meeting will be arranged in the week commencing 15 September 2026, at which the transition plan submitted with your tender will be baselined.`);
  L.push('');
  L.push(`Yours faithfully`);
  L.push('');
  L.push(`**R. Awoyemi**  `);
  L.push(`Procurement Lead, ${tender.buyer}  `);
  L.push(`procurement@aldercroft.gov.uk`);
  return L.join('\n');
}

export function buildContractDetailsNotice(bidderId: string, bands: BandMap, tender: any): string {
  const ranked = rankAll(bands).filter(r => !r.disqualified);
  const me = ranked.find(r => r.bidderId === bidderId);
  if (!me) return '';
  const b = bidders.find(x => x.id === bidderId)!;
  const obligations = postAwardObligations(me.price);
  const L: string[] = [];
  L.push(`# Contract Details Notice`);
  L.push('');
  L.push(`> Published to Find a Tender under section 53 of the Procurement Act 2023, within 30 days of the contract being entered into.`);
  L.push('');
  L.push(`## Section 1 — Contracting authority`);
  L.push('');
  L.push(`| Field | Value |`);
  L.push(`| --- | --- |`);
  L.push(`| Authority | ${tender.buyer} |`);
  L.push(`| Type | ${tender.buyerType} |`);
  L.push(`| Contact | procurement@aldercroft.gov.uk |`);
  L.push('');
  L.push(`## Section 2 — Contract`);
  L.push('');
  L.push(`| Field | Value |`);
  L.push(`| --- | --- |`);
  L.push(`| Title | ${tender.title} |`);
  L.push(`| Reference | ${tender.reference} |`);
  L.push(`| Tender notice | ${tender.ftsNotice} |`);
  L.push(`| Procedure | ${tender.procedure} |`);
  L.push(`| Award basis | ${tender.awardBasis} |`);
  L.push(`| Final contract value | ${gbp(me.price)} |`);
  L.push(`| Duration | ${tender.term} |`);
  L.push(`| Commencement | 1 October 2026 |`);
  L.push('');
  L.push(`## Section 3 — Supplier`);
  L.push('');
  L.push(`| Field | Value |`);
  L.push(`| --- | --- |`);
  L.push(`| Name | ${b.name} |`);
  L.push(`| Companies House number | ${b.companyNumber} |`);
  L.push(`| Registered location | ${b.hq} |`);
  L.push(`| SME status | ${b.smeStatus} |`);
  L.push('');
  L.push(`## Section 4 — Procedure and outcome`);
  L.push('');
  L.push(`| Field | Value |`);
  L.push(`| --- | --- |`);
  L.push(`| Tenders received | ${bidders.length} |`);
  L.push(`| Tenders assessed | ${ranked.length} |`);
  L.push(`| Tenders disregarded | ${bidders.length - ranked.length} |`);
  L.push(`| Contract award notice published | 2 September 2026 |`);
  L.push(`| Standstill ended | 11 September 2026 |`);
  L.push('');
  L.push(`## Section 5 — Transparency obligations for this contract`);
  L.push('');
  L.push(`| Obligation | Basis | Applies | When |`);
  L.push(`| --- | --- | :---: | --- |`);
  obligations.forEach(o => L.push(`| ${o.label} | ${o.basis} | ${o.applies ? 'Yes' : 'No'} | ${o.applies ? o.due : o.why} |`));
  L.push('');
  L.push(`A copy of the contract is not published, and key performance indicators are not set, because the contract value of ${gbp(me.price)} does not exceed the £5 million threshold at sections 52 and 53(3).`);
  return L.join('\n');
}
